export const INVITED_BY_QUERY_PARAM = "invited_by";

export const PARTY_JOIN_METHODS = [
  "created",
  "invite_link",
  "email",
  "friend_invite",
] as const;

export type PartyJoinMethod = (typeof PARTY_JOIN_METHODS)[number];

export type InvitedByKind = "owner" | "member";

export type PartyJoinAttribution = {
  joinMethod: PartyJoinMethod;
  invitedByUserId: string | null;
  invitedByKind: InvitedByKind | null;
};

export type PartyJoinAttributionSummary = {
  total: number;
  created: number;
  invitedByOwner: number;
  invitedByMember: number;
  unknownInviter: number;
  byMethod: Record<PartyJoinMethod | "unknown", number>;
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function parseInvitedByUserId(
  value: string | string[] | null | undefined,
): string | null {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = raw?.trim() ?? "";
  if (!UUID_PATTERN.test(trimmed)) {
    return null;
  }
  return trimmed.toLowerCase();
}

export function withInvitedByUser(
  url: string,
  userId: string | null | undefined,
): string {
  const invitedByUserId = parseInvitedByUserId(userId);
  if (!invitedByUserId) {
    return url;
  }

  try {
    const parsed = new URL(url);
    parsed.searchParams.set(INVITED_BY_QUERY_PARAM, invitedByUserId);
    return parsed.toString();
  } catch {
    return url;
  }
}

export function inviterKind(
  ownerId: string,
  invitedByUserId: string | null | undefined,
): InvitedByKind | null {
  const invitedBy = invitedByUserId?.trim() ?? "";
  if (!invitedBy) {
    return null;
  }
  return invitedBy === ownerId ? "owner" : "member";
}

export function partyJoinAttribution(input: {
  joinMethod: PartyJoinMethod;
  ownerId: string;
  invitedByUserId?: string | null;
}): PartyJoinAttribution {
  if (input.joinMethod === "created") {
    return {
      joinMethod: "created",
      invitedByUserId: null,
      invitedByKind: null,
    };
  }

  const invitedByUserId = input.invitedByUserId?.trim() || null;
  return {
    joinMethod: input.joinMethod,
    invitedByUserId,
    invitedByKind: inviterKind(input.ownerId, invitedByUserId),
  };
}

export function summarizePartyJoinAttribution(
  ownerId: string,
  participants: readonly {
    joinMethod?: PartyJoinMethod | null;
    invitedByUserId?: string | null;
  }[],
): PartyJoinAttributionSummary {
  const summary: PartyJoinAttributionSummary = {
    total: 0,
    created: 0,
    invitedByOwner: 0,
    invitedByMember: 0,
    unknownInviter: 0,
    byMethod: {
      created: 0,
      invite_link: 0,
      email: 0,
      friend_invite: 0,
      unknown: 0,
    },
  };

  for (const participant of participants) {
    summary.total += 1;
    const method = participant.joinMethod ?? "unknown";
    summary.byMethod[method] += 1;
    if (method === "created") {
      summary.created += 1;
      continue;
    }

    const kind = inviterKind(ownerId, participant.invitedByUserId);
    if (kind === "owner") {
      summary.invitedByOwner += 1;
    } else if (kind === "member") {
      summary.invitedByMember += 1;
    } else {
      summary.unknownInviter += 1;
    }
  }

  return summary;
}

export type PartyJoinedReferralSource =
  | "invite_link"
  | "email_invitation"
  | "friend_invite"
  | "created";

export function partyJoinedReferralSource(
  joinMethod: PartyJoinMethod,
): PartyJoinedReferralSource {
  switch (joinMethod) {
    case "email":
      return "email_invitation";
    case "friend_invite":
      return "friend_invite";
    case "created":
      return "created";
    case "invite_link":
      return "invite_link";
  }
}

export function buildPartyJoinedEventProperties(input: {
  partyId: string;
  referralSource: PartyJoinedReferralSource;
  attribution?: PartyJoinAttribution | null;
}): {
  party_id: string;
  referral_source: PartyJoinedReferralSource;
  join_method: PartyJoinMethod | null;
  invited_by_user_id: string | null;
  invited_by_is_owner: boolean | null;
} {
  const attribution = input.attribution ?? null;
  return {
    party_id: input.partyId,
    referral_source: input.referralSource,
    join_method: attribution?.joinMethod ?? null,
    invited_by_user_id: attribution?.invitedByUserId ?? null,
    invited_by_is_owner:
      attribution == null ? null : attribution.invitedByKind === "owner",
  };
}
